import { useParams, useNavigate, Link } from 'react-router';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { appointmentsApi } from '@/api/appointments';
import { customersApi, type CustomerAppointment } from '@/api/customers';
import { useBusinessId } from '@/hooks/useBusinessId';
import { formatTime } from '@/utils/time';
import { formatDateShort } from '@/utils/date';
import { StatusBadge, type StatusType } from '../components/status-badge';
import { CheckoutDetailsModal } from '../components/checkout-details-modal';
import { Star, Pencil, X, Clock, History, Dna, User, Loader2, AlertTriangle, FileCheck2, FileText } from 'lucide-react';

/** Map backend stateCode to the StatusBadge type used by the UI */
function mapStateCode(stateCode: string): StatusType {
  switch (stateCode) {
    case 'CONFIRMED': return 'confirmed';
    case 'COMPLETED': return 'completed';
    case 'CANCELLED': return 'cancelled';
    case 'NO_SHOW': return 'noshow';
    case 'EXPIRED': return 'expired';
    case 'PENDING': return 'pending';
    case 'IN_PROGRESS': return 'in-progress';
    default: return 'pending';
  }
}

export function AppointmentDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const businessId = useBusinessId();
  const queryClient = useQueryClient();

  const [starred, setStarred] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showCheckoutDetails, setShowCheckoutDetails] = useState(false);

  const appointmentId = Number(id);

  // Fetch the appointment
  const { data: appointment, isLoading: appointmentLoading, isError: appointmentError } = useQuery({
    queryKey: ['appointment', businessId, appointmentId],
    queryFn: () => appointmentsApi.getById(businessId, appointmentId),
    enabled: !!businessId && !!appointmentId,
  });

  // Fetch customer history once we have customerId
  const { data: customerHistory = [] } = useQuery({
    queryKey: ['customerAppointments', businessId, appointment?.customerId],
    queryFn: () => customersApi.getAppointments(businessId, appointment!.customerId),
    enabled: !!businessId && !!appointment?.customerId,
  });

  // State transition mutation
  const transitionMutation = useMutation({
    mutationFn: (args: { stateCode: "CONFIRMED" | "COMPLETED" | "CANCELLED" | "NO_SHOW" | "PENDING"; cancelledBy?: "CUSTOMER" | "BUSINESS"; cancellationReason?: string }) =>
      appointmentsApi.transitionState(businessId, appointmentId, args),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['appointment', businessId, appointmentId] });
      queryClient.invalidateQueries({ queryKey: ['customerAppointments', businessId, appointment?.customerId] });
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      setShowCancelConfirm(false);
      setShowCheckoutDetails(false);
      const msg = vars.stateCode === 'COMPLETED' ? 'Cita completada'
        : vars.stateCode === 'CONFIRMED' ? 'Cita confirmada'
        : vars.stateCode === 'CANCELLED' ? 'Cita cancelada'
        : 'Estado actualizado';
      toast.success(msg);
    },
    onError: () => toast.error('No se pudo actualizar la cita'),
  });

  if (appointmentLoading) {
    return (
      <div className="h-full flex items-center justify-center" style={{ backgroundColor: 'var(--cream)' }}>
        <Loader2 size={32} className="animate-spin" style={{ color: 'var(--terracotta)' }} />
      </div>
    );
  }

  if (appointmentError || !appointment) {
    return <div className="p-6" style={{ color: 'var(--dark-teal)' }}>Cita no encontrada</div>;
  }

  const uiStatus = mapStateCode(appointment.stateCode);

  // Completed visits from customer history
  const completedVisits = customerHistory.filter((a: CustomerAppointment) => a.stateCode === 'COMPLETED');

  // Service name: prefer services array if present, else serviceName
  const serviceName = appointment.services && appointment.services.length > 0
    ? appointment.services.map(s => s.serviceName || s.servicePackageName).filter(Boolean).join(', ')
    : appointment.serviceName;

  const getInitials = (name: string) =>
    name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  const getPrimaryAction = () => {
    if (transitionMutation.isPending) {
      return { label: 'Procesando...', bg: 'var(--muted-bg)', color: 'var(--muted-fg)', disabled: true, action: () => {} };
    }
    switch (appointment.stateCode) {
      case 'PENDING':
        return {
          label: 'Confirmar',
          bg: 'var(--terracotta)',
          color: 'white',
          disabled: false,
          action: () => transitionMutation.mutate({ stateCode: 'CONFIRMED' }),
        };
      case 'CONFIRMED':
        return {
          label: 'Completar',
          bg: 'var(--dark-teal)',
          color: 'white',
          disabled: false,
          action: () => setShowCheckoutDetails(true),
        };
      case 'COMPLETED':
        return { label: 'Completada', bg: 'var(--muted-bg)', color: 'var(--muted-fg)', disabled: true, action: () => {} };
      case 'CANCELLED':
        return { label: 'Cancelada', bg: 'var(--muted-bg)', color: 'var(--muted-fg)', disabled: true, action: () => {} };
      case 'NO_SHOW':
        return { label: 'No asistió', bg: 'var(--muted-bg)', color: 'var(--muted-fg)', disabled: true, action: () => {} };
      case 'EXPIRED':
        return { label: 'Expirada', bg: 'var(--muted-bg)', color: 'var(--muted-fg)', disabled: true, action: () => {} };
      default:
        return {
          label: 'Confirmar',
          bg: 'var(--terracotta)',
          color: 'white',
          disabled: false,
          action: () => transitionMutation.mutate({ stateCode: 'CONFIRMED' }),
        };
    }
  };

  const primaryAction = getPrimaryAction();

  const handleCancelConfirm = () => {
    transitionMutation.mutate({ stateCode: 'CANCELLED', cancelledBy: 'BUSINESS' });
  };

  const isTerminal = appointment.stateCode === 'CANCELLED' || appointment.stateCode === 'COMPLETED';

  /* --- Action bar (shared) --- */
  const actionBar = (
    <div className="flex items-center gap-2 pt-3 flex-shrink-0" style={{ borderTop: '1px solid var(--border-color)' }}>
      <button disabled={primaryAction.disabled} className="flex-1 rounded-lg transition-colors flex items-center justify-center gap-2"
        style={{ height: '42px', backgroundColor: primaryAction.bg, color: primaryAction.color, fontFamily: 'var(--font-body)', fontSize: '14px', fontWeight: 500, cursor: primaryAction.disabled ? 'not-allowed' : 'pointer', borderRadius: 'var(--radius-md)' }}
        onClick={primaryAction.action}>
        {transitionMutation.isPending && <Loader2 size={14} className="animate-spin" />}
        {primaryAction.label}
      </button>
      <button onClick={() => setStarred(!starred)} className="flex items-center justify-center rounded-lg transition-colors" title="Favorito"
        style={{ width: '42px', height: '42px', border: `1px solid ${starred ? 'var(--terracotta)' : 'var(--border-color)'}`, backgroundColor: starred ? 'rgba(224, 123, 84, 0.08)' : 'transparent', borderRadius: 'var(--radius-md)' }}>
        <Star size={16} fill={starred ? 'var(--terracotta)' : 'none'} stroke={starred ? 'var(--terracotta)' : 'var(--muted-fg)'} strokeWidth={1.5} />
      </button>
      <button onClick={() => navigate(`/appointment/${appointment.id}/edit`)} className="flex items-center justify-center rounded-lg transition-colors hover:bg-[#FAF9F7]" title="Editar"
        style={{ width: '42px', height: '42px', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)' }}>
        <Pencil size={15} stroke="var(--muted-fg)" strokeWidth={1.5} />
      </button>
      <button onClick={() => setShowCancelConfirm(true)} disabled={isTerminal}
        className="flex items-center justify-center rounded-lg transition-colors hover:bg-red-50" title="Cancelar"
        style={{ width: '42px', height: '42px', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', cursor: isTerminal ? 'not-allowed' : 'pointer', opacity: isTerminal ? 0.4 : 1 }}>
        <X size={16} stroke="var(--destructive)" strokeWidth={1.5} />
      </button>
    </div>
  );

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--cream)' }}>

      {/* MOBILE LAYOUT (< 768px) */}
      <div className="md:hidden flex-1 flex flex-col overflow-hidden">
        <div className="flex-1 overflow-auto pb-24">
          {/* Compact Client Header */}
          <div style={{ backgroundColor: 'var(--dark-teal)', padding: '14px 16px' }}>
            <div className="flex items-center gap-3">
              <div className="rounded-full flex items-center justify-center flex-shrink-0" style={{ width: '44px', height: '44px', backgroundColor: 'var(--terracotta)' }}>
                <span className="text-white" style={{ fontFamily: 'var(--font-heading)', fontSize: '16px' }}>{getInitials(appointment.customerName)}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-white truncate" style={{ fontFamily: 'var(--font-heading)', fontSize: '16px' }}>{appointment.customerName}</span>
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>{completedVisits.length} visitas</span>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Content */}
          <div style={{ padding: '12px 14px 24px' }}>
            {/* Service Card */}
            <div className="rounded-xl" style={{
              backgroundColor: 'var(--off-white)',
              border: uiStatus === 'in-progress' ? '2px solid var(--terracotta)' : '1px solid var(--border-color)',
              boxShadow: uiStatus === 'in-progress' ? '0 2px 12px rgba(224, 123, 84, 0.12)' : 'var(--shadow-card)',
              padding: '14px 16px', marginBottom: '10px'
            }}>
              <div className="flex items-start justify-between mb-1.5">
                <div style={{ fontFamily: 'var(--font-heading)', fontSize: '17px', color: 'var(--dark-teal)' }}>{serviceName}</div>
                <StatusBadge status={uiStatus} />
              </div>
              <div className="flex items-center gap-2 flex-wrap" style={{ fontSize: '12px', color: 'var(--muted-fg)' }}>
                <span className="flex items-center gap-1"><Clock size={12} />{formatTime(appointment.startTime)}</span>
                <span style={{ opacity: 0.3 }}>·</span>
                <span>{appointment.duration} min</span>
                <span style={{ opacity: 0.3 }}>·</span>
                <span className="flex items-center gap-1"><User size={11} />{appointment.userName}</span>
                <span style={{ opacity: 0.3 }}>·</span>
                <span>{formatDateShort(appointment.appointmentDate)}</span>
              </div>
            </div>

            {/* Cancellation info */}
            {appointment.stateCode === 'CANCELLED' && appointment.cancellationReason && (
              <div className="rounded-xl" style={{ backgroundColor: 'var(--off-white)', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-card)', padding: '12px 14px', marginBottom: '10px' }}>
                <div className="section-label" style={{ marginBottom: '4px' }}>Motivo de cancelación</div>
                <div style={{ fontSize: '13px', color: 'var(--dark-teal)', lineHeight: 1.5 }}>
                  {appointment.cancelledBy === 'CUSTOMER' ? 'Cancelado por el cliente' : 'Cancelado por el negocio'}
                  {appointment.cancellationReason && ` — ${appointment.cancellationReason}`}
                </div>
              </div>
            )}

            {/* Consent documents */}
            {appointment.consents && appointment.consents.length > 0 && (() => {
              const pending = appointment.consents!.filter(c => c.status === 'PENDING');
              return (
                <>
                  {pending.length > 0 && (
                    <div className="rounded-xl flex items-start gap-2.5" style={{
                      backgroundColor: '#FFFBEB', border: '1px solid #FDE68A',
                      padding: '12px 14px', marginBottom: '10px'
                    }}>
                      <AlertTriangle size={16} style={{ color: '#D97706', marginTop: '1px', flexShrink: 0 }} />
                      <span className="min-w-0 break-words" style={{ fontSize: '13px', color: '#92400E', lineHeight: 1.5 }}>
                        El cliente no ha firmado: {pending.map(c => c.consentFormName).join(', ')}
                      </span>
                    </div>
                  )}
                  <div className="rounded-xl" style={{
                    backgroundColor: 'var(--off-white)', border: '1px solid var(--border-color)',
                    boxShadow: 'var(--shadow-card)', padding: '12px 14px', marginBottom: '10px'
                  }}>
                    <div className="section-label" style={{ marginBottom: '6px' }}>Consentimientos</div>
                    <div className="space-y-2">
                      {appointment.consents!.map(consent => (
                        <div key={consent.id} className="flex items-center justify-between" style={{ fontSize: '12px' }}>
                          <div className="flex items-center gap-2 min-w-0">
                            {consent.status === 'SIGNED'
                              ? <FileCheck2 size={14} style={{ color: '#252E3E', flexShrink: 0 }} />
                              : <FileText size={14} style={{ color: '#D97706', flexShrink: 0 }} />
                            }
                            <span className="truncate" style={{ color: 'var(--dark-teal)' }}>
                              {consent.consentFormName}
                            </span>
                          </div>
                          <span style={{
                            fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '9999px',
                            ...(consent.status === 'SIGNED'
                              ? { backgroundColor: 'rgba(37, 46, 62, 0.08)', color: '#252E3E' }
                              : { backgroundColor: '#FFFBEB', color: '#92400E' })
                          }}>
                            {consent.status === 'SIGNED' ? 'Firmado' : 'Pendiente'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              );
            })()}

            {/* Services breakdown if multiple */}
            {appointment.services && appointment.services.length > 1 && (
              <div className="rounded-xl" style={{ backgroundColor: 'var(--off-white)', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-card)', padding: '12px 14px', marginBottom: '10px' }}>
                <div className="section-label" style={{ marginBottom: '6px' }}>Servicios</div>
                <div className="space-y-1">
                  {appointment.services.map((s, i) => (
                    <div key={i} className="flex items-center justify-between" style={{ fontSize: '12px' }}>
                      <span style={{ color: 'var(--dark-teal)' }}>{s.serviceName || s.servicePackageName}</span>
                      <span style={{ color: 'var(--muted-fg)' }}>{s.durationMinutes} min</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Visits */}
            <div className="rounded-xl" style={{ backgroundColor: 'var(--off-white)', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-card)', padding: '12px 14px', marginBottom: '10px' }}>
              <div className="section-label" style={{ marginBottom: '6px' }}>Visitas recientes</div>
              {completedVisits.length > 0 ? (
                <div className="space-y-0">
                  {completedVisits.slice(0, 3).map((apt: CustomerAppointment, i: number) => (
                    <div key={apt.id} className="flex items-center justify-between" style={{ padding: '4px 0', borderBottom: i < Math.min(completedVisits.length, 3) - 1 ? '1px solid var(--border-color)' : 'none' }}>
                      <span style={{ fontSize: '12px', color: 'var(--dark-teal)' }}>{apt.serviceName}</span>
                      <span style={{ fontSize: '11px', color: 'var(--muted-fg)' }}>{formatDateShort(apt.appointmentDate)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: '12px', color: 'var(--muted-fg)' }}>Sin visitas previas</div>
              )}
            </div>

            {/* Quick links */}
            <div className="flex gap-2">
              <Link to={`/customer/${appointment.customerId}/history`} className="flex-1 flex items-center justify-center gap-1.5 rounded-lg"
                style={{ height: '36px', border: '1px solid var(--border-color)', fontSize: '12px', color: 'var(--muted-fg)', fontWeight: 500, backgroundColor: 'var(--off-white)' }}>
                <History size={13} /> Historial
              </Link>
              <Link to={`/client-dna/${appointment.customerId}`} className="flex-1 flex items-center justify-center gap-1.5 rounded-lg"
                style={{ height: '36px', border: '1px solid var(--border-color)', fontSize: '12px', color: 'var(--muted-fg)', fontWeight: 500, backgroundColor: 'var(--off-white)' }}>
                <Dna size={13} /> ADN del cliente
              </Link>
            </div>
          </div>
        </div>

        {/* Mobile Action Bar */}
        <div className="flex-shrink-0" style={{ padding: '8px 14px 12px', backgroundColor: 'var(--off-white)', borderTop: '1px solid var(--border-color)' }}>
          {actionBar}
        </div>
      </div>

      {/* DESKTOP/TABLET LAYOUT (>= 768px) */}
      <div className="hidden md:flex flex-1 overflow-hidden">
        {/* LEFT -- Client Spotlight */}
        <div className="flex flex-col overflow-auto" style={{ width: '340px', minWidth: '340px', backgroundColor: 'var(--dark-teal)', padding: '24px 20px 16px' }}>
          <div className="flex flex-col items-center mb-4">
            <div className="rounded-full flex items-center justify-center" style={{ width: '88px', height: '88px', backgroundColor: 'var(--terracotta)' }}>
              <span className="text-white" style={{ fontFamily: 'var(--font-heading)', fontSize: '28px' }}>{getInitials(appointment.customerName)}</span>
            </div>
          </div>

          <div className="text-center mb-4">
            <div className="text-white mb-1.5" style={{ fontFamily: 'var(--font-heading)', fontSize: '20px' }}>{appointment.customerName}</div>
            <div className="flex items-center justify-center gap-2 mb-2">
              <Link to={`/customer/${appointment.customerId}/history`} className="flex items-center gap-1 rounded-full transition-colors hover:bg-white/10"
                style={{ padding: '2px 8px', fontSize: '10px', fontWeight: 500, color: 'rgba(255,255,255,0.7)', backgroundColor: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)' }}>
                <History size={10} /> Historial
              </Link>
              <Link to={`/client-dna/${appointment.customerId}`} className="flex items-center gap-1 rounded-full transition-colors hover:bg-white/10"
                style={{ padding: '2px 8px', fontSize: '10px', fontWeight: 500, color: 'rgba(255,255,255,0.7)', backgroundColor: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)' }}>
                <Dna size={10} /> ADN
              </Link>
            </div>
          </div>

          <div className="rounded-lg mb-3" style={{ backgroundColor: 'rgba(255,255,255,0.06)', padding: '10px 12px' }}>
            <div className="space-y-2">
              <div className="flex justify-between" style={{ fontSize: '12px' }}>
                <span style={{ color: 'rgba(255,255,255,0.45)' }}>Total visitas</span>
                <span style={{ color: 'rgba(255,255,255,0.8)' }}>{completedVisits.length}</span>
              </div>
              <div className="flex justify-between" style={{ fontSize: '12px' }}>
                <span style={{ color: 'rgba(255,255,255,0.45)' }}>Profesional</span>
                <span style={{ color: 'rgba(255,255,255,0.8)' }}>{appointment.userName}</span>
              </div>
            </div>
          </div>

          <div className="mt-auto flex gap-2">
            <Link to={`/customer/${appointment.customerId}/history`} className="flex-1 flex items-center justify-center gap-1.5 rounded-lg transition-colors hover:bg-white/10" style={{ height: '36px', border: '1px solid rgba(255,255,255,0.15)', fontSize: '12px', color: 'rgba(255,255,255,0.7)', fontWeight: 500 }}>
              <History size={13} /> Historial
            </Link>
            <Link to={`/client-dna/${appointment.customerId}`} className="flex-1 flex items-center justify-center gap-1.5 rounded-lg transition-colors hover:bg-white/10" style={{ height: '36px', border: '1px solid rgba(255,255,255,0.15)', fontSize: '12px', color: 'rgba(255,255,255,0.7)', fontWeight: 500 }}>
              <Dna size={13} /> ADN del cliente
            </Link>
          </div>
        </div>

        {/* RIGHT -- Service + Details */}
        <div className="flex-1 flex flex-col overflow-auto" style={{ padding: '20px 24px 16px' }}>
          <div className="rounded-xl mb-4" style={{ backgroundColor: 'var(--off-white)', border: uiStatus === 'in-progress' ? '2px solid var(--terracotta)' : '1px solid var(--border-color)', boxShadow: uiStatus === 'in-progress' ? '0 2px 12px rgba(224, 123, 84, 0.12)' : 'var(--shadow-card)', padding: '16px 20px' }}>
            <div className="flex items-start justify-between mb-2">
              <div>
                <div style={{ fontFamily: 'var(--font-heading)', fontSize: '20px', color: 'var(--dark-teal)', marginBottom: '4px' }}>{serviceName}</div>
                <div className="flex items-center gap-3" style={{ fontSize: '13px', color: 'var(--muted-fg)' }}>
                  <span className="flex items-center gap-1"><Clock size={13} />{formatTime(appointment.startTime)}</span>
                  <span style={{ opacity: 0.3 }}>|</span>
                  <span>{appointment.duration} min</span>
                  <span style={{ opacity: 0.3 }}>|</span>
                  <span className="flex items-center gap-1"><User size={12} />{appointment.userName}</span>
                  <span style={{ opacity: 0.3 }}>|</span>
                  <span>{formatDateShort(appointment.appointmentDate)}</span>
                </div>
              </div>
              <StatusBadge status={uiStatus} />
            </div>
          </div>

          {/* Cancellation info */}
          {appointment.stateCode === 'CANCELLED' && appointment.cancellationReason && (
            <div className="rounded-xl mb-4" style={{ backgroundColor: 'var(--off-white)', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-card)', padding: '14px 20px' }}>
              <div className="section-label" style={{ marginBottom: '6px' }}>Motivo de cancelación</div>
              <div style={{ fontSize: '14px', color: 'var(--dark-teal)', lineHeight: 1.55 }}>
                {appointment.cancelledBy === 'CUSTOMER' ? 'Cancelado por el cliente' : 'Cancelado por el negocio'}
                {appointment.cancellationReason && ` — ${appointment.cancellationReason}`}
              </div>
            </div>
          )}

          {/* Consent documents */}
          {appointment.consents && appointment.consents.length > 0 && (() => {
            const pending = appointment.consents!.filter(c => c.status === 'PENDING');
            return (
              <>
                {pending.length > 0 && (
                  <div className="rounded-xl flex items-start gap-2.5 mb-4" style={{
                    backgroundColor: '#FFFBEB', border: '1px solid #FDE68A',
                    padding: '14px 20px'
                  }}>
                    <AlertTriangle size={16} style={{ color: '#D97706', marginTop: '1px', flexShrink: 0 }} />
                    <span className="min-w-0 break-words" style={{ fontSize: '14px', color: '#92400E', lineHeight: 1.5 }}>
                      El cliente no ha firmado: {pending.map(c => c.consentFormName).join(', ')}
                    </span>
                  </div>
                )}
                <div className="rounded-xl mb-4" style={{
                  backgroundColor: 'var(--off-white)', border: '1px solid var(--border-color)',
                  boxShadow: 'var(--shadow-card)', padding: '14px 20px'
                }}>
                  <div className="section-label" style={{ marginBottom: '8px' }}>Consentimientos</div>
                  <div className="space-y-2">
                    {appointment.consents!.map(consent => (
                      <div key={consent.id} className="flex items-center justify-between" style={{ fontSize: '13px' }}>
                        <div className="flex items-center gap-2 min-w-0">
                          {consent.status === 'SIGNED'
                            ? <FileCheck2 size={15} style={{ color: '#252E3E', flexShrink: 0 }} />
                            : <FileText size={15} style={{ color: '#D97706', flexShrink: 0 }} />
                          }
                          <span className="truncate" style={{ color: 'var(--dark-teal)' }}>
                            {consent.consentFormName}
                          </span>
                        </div>
                        <span style={{
                          fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '9999px',
                          ...(consent.status === 'SIGNED'
                            ? { backgroundColor: 'rgba(37, 46, 62, 0.08)', color: '#252E3E' }
                            : { backgroundColor: '#FFFBEB', color: '#92400E' })
                        }}>
                          {consent.status === 'SIGNED' ? 'Firmado' : 'Pendiente'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            );
          })()}

          {/* Services breakdown if multiple */}
          {appointment.services && appointment.services.length > 1 && (
            <div className="rounded-xl mb-4" style={{ backgroundColor: 'var(--off-white)', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-card)', padding: '14px 20px' }}>
              <div className="section-label" style={{ marginBottom: '8px' }}>Servicios</div>
              <div className="space-y-2">
                {appointment.services.map((s, i) => (
                  <div key={i} className="flex items-center justify-between" style={{ fontSize: '13px' }}>
                    <span style={{ color: 'var(--dark-teal)' }}>{s.serviceName || s.servicePackageName}</span>
                    <span style={{ color: 'var(--muted-fg)' }}>{s.durationMinutes} min</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="rounded-xl mb-4 flex-1" style={{ backgroundColor: 'var(--off-white)', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-card)', padding: '14px 20px', minHeight: 0 }}>
            <div className="section-label" style={{ marginBottom: '8px' }}>Visitas recientes</div>
            {completedVisits.length > 0 ? (
              <div className="space-y-0">
                {completedVisits.slice(0, 5).map((apt: CustomerAppointment, i: number) => (
                  <div key={apt.id} className="flex items-center justify-between" style={{ padding: '5px 0', borderBottom: i < Math.min(completedVisits.length, 5) - 1 ? '1px solid var(--border-color)' : 'none' }}>
                    <div className="flex items-center gap-3">
                      <span style={{ fontSize: '12px', color: 'var(--muted-fg)', width: '70px' }}>{formatDateShort(apt.appointmentDate)}</span>
                      <span style={{ fontSize: '13px', color: 'var(--dark-teal)' }}>{apt.serviceName}</span>
                    </div>
                    <span style={{ fontSize: '12px', color: 'var(--muted-fg)' }}>{apt.userName}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ fontSize: '13px', color: 'var(--muted-fg)' }}>Sin visitas previas</div>
            )}
          </div>

          {actionBar}
        </div>
      </div>

      {/* Cancel Confirmation */}
      {showCancelConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="rounded-2xl p-6 md:p-8 max-w-md mx-4" style={{ backgroundColor: 'var(--off-white)', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-lg)' }}>
            <h2 className="mb-4" style={{ fontFamily: 'var(--font-heading)', fontSize: '20px', color: 'var(--dark-teal)' }}>¿Cancelar cita?</h2>
            <div className="mb-6" style={{ fontSize: '14px', color: 'var(--muted-fg)', lineHeight: 1.5 }}>
              Se cancelará la cita de {appointment.customerName} el {formatDateShort(appointment.appointmentDate)} a las {formatTime(appointment.startTime)}.
            </div>
            <div className="flex gap-3">
              <button onClick={handleCancelConfirm} disabled={transitionMutation.isPending}
                className="flex-1 rounded-lg flex items-center justify-center gap-2"
                style={{ height: '42px', backgroundColor: 'var(--destructive)', color: 'white', fontSize: '14px', fontWeight: 500 }}>
                {transitionMutation.isPending && <Loader2 size={14} className="animate-spin" />}
                Sí, cancelar
              </button>
              <button onClick={() => setShowCancelConfirm(false)} className="rounded-lg"
                style={{ height: '42px', padding: '0 20px', border: '1px solid var(--border-color)', color: 'var(--dark-teal)', fontSize: '14px' }}>
                Volver
              </button>
            </div>
          </div>
        </div>
      )}

      <CheckoutDetailsModal
        open={showCheckoutDetails}
        onClose={() => setShowCheckoutDetails(false)}
        businessId={businessId}
        appointmentId={appointmentId}
        completing={transitionMutation.isPending}
        onComplete={() => transitionMutation.mutate({ stateCode: 'COMPLETED' })}
      />
    </div>
  );
}
